import { type RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';

import { type Reward, type RewardDetail } from '@/common/types/earnRewardsType';
import { type User } from '@/common/types/userType';

const user = (state: RootState): User | null => state.user.user;
const selectAllRewards = (
  state: RootState,
): Reward[] | RewardDetail | undefined => {
  return state.earnRewards.rewards?.rewards;
};

const getLastUserReward = createSelector(
  selectAllRewards,
  (allRewards): Reward | undefined => {
    const rewardsArray =
      allRewards && 'earnRewards' in allRewards
        ? allRewards.earnRewards
        : Array.isArray(allRewards)
          ? allRewards
          : undefined;

    if (rewardsArray) {
      return rewardsArray
        .slice()
        .reverse()
        .find((reward) => reward.claimAt);
    }
  },
);

const getActiveReward = createSelector(
  selectAllRewards,
  getLastUserReward,
  (allRewards, lastUserReward) => {
    const rewardsArray =
      allRewards && 'earnRewards' in allRewards
        ? allRewards.earnRewards
        : Array.isArray(allRewards)
          ? allRewards
          : undefined;

    if (allRewards && 'cost' in allRewards && !allRewards.buyAt) {
      return;
    }

    if (lastUserReward && rewardsArray) {
      return rewardsArray.find(
        (el: Reward) => el.rewardId === lastUserReward.rewardId + 1,
      );
    }

    if (rewardsArray) {
      return rewardsArray[0];
    }
  },
);

const getPaidRewardPlanCost = createSelector(selectAllRewards, (allRewards) => {
  if (allRewards && 'cost' in allRewards) {
    return allRewards.cost;
  }
});

const getIsBoughtPlan = createSelector(selectAllRewards, (allRewards) => {
  if (allRewards && 'buyAt' in allRewards) {
    return !!allRewards.buyAt;
  }
});

const getIsFinishedPlan = createSelector(
  selectAllRewards,
  (state: RootState) => state.earnRewards.rewards?.nextClaimTime,
  (allRewards, claimTime) => {
    if (typeof claimTime === 'undefined') return false;

    if (Array.isArray(allRewards)) {
      return allRewards.every((reward) => reward.claimAt);
    }

    return allRewards?.cost && !allRewards.buyAt;
  },
);

const checkLevelToGetRewards = createSelector(
  user,
  (user) => !!user && user.level >= user.settings.minLevelToGetReward,
);

export {
  checkLevelToGetRewards,
  getActiveReward,
  getIsBoughtPlan,
  getIsFinishedPlan,
  getLastUserReward,
  getPaidRewardPlanCost,
};
